/**
 * Whiteboard Real-time Collaboration
 * static/js/whiteboard/collaboration.js
 * 
 * 使用 Socket.IO 提供即時協作功能
 * 支援訪客模式：掃 QR 碼輸入姓名即可加入協作
 */

import * as state from './state.js';
import * as ui from './ui.js';

// Socket.IO 連接
let socket = null;
let currentRoomId = null;
let collaborators = new Map(); // socketId -> collaborator info
let isConnected = false;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 5;

// 訪客/主持人狀態
let isGuestMode = false;
let isOwner = false;
let ownerId = null;
let guestName = null;

// 廣播控制
let broadcastPaused = false;  // 暫停廣播（房間建立時用）

// 雷射筆狀態
let laserPointerActive = false;
let previousTool = null;

// 其他協作者的游標元素
const cursorElements = new Map();

/**
 * 初始化協作功能
 */
export async function init() {
    // 載入 Socket.IO 客戶端
    await loadSocketIO();
    
    // 檢查是否為訪客模式（從模板傳入）
    isGuestMode = window.WHITEBOARD_GUEST_MODE === true;
    
    // 檢查 URL 參數是否有房間 ID
    const urlParams = new URLSearchParams(window.location.search);
    const roomId = urlParams.get('room');
    
    if (roomId) {
        // 自動加入房間
        await connect();
        
        if (isGuestMode) {
            // 訪客自動生成名稱並加入
            guestName = generateGuestName();
            console.log('[Collaboration] Guest auto-joining as:', guestName);
            await joinRoom(roomId, guestName);
        } else {
            // 已登入用戶直接加入
            await joinRoom(roomId);
        }
    }
    
    // 設定主持人專屬按鈕
    setupOwnerControls();
    
    console.log('[Collaboration] Initialized, guest mode:', isGuestMode);
}

/**
 * 生成訪客名稱
 */
function generateGuestName() {
    const adjectives = ['快樂', '聰明', '勇敢', '活潑', '可愛', '認真', '努力', '開心'];
    const nouns = ['小貓', '小狗', '小鳥', '小熊', '小兔', '小魚', '小象', '小鹿'];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const num = Math.floor(Math.random() * 100);
    return `${adj}${noun}${num}`;
}

/**
 * 載入 Socket.IO 客戶端
 */
async function loadSocketIO() {
    if (window.io) {
        return;
    }
    
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/static/lib/socket.io.min.js';
        script.onload = () => {
            console.log('[Collaboration] Socket.IO loaded');
            resolve();
        };
        script.onerror = () => {
            // 嘗試 CDN
            const cdnScript = document.createElement('script');
            cdnScript.src = 'https://cdn.socket.io/4.7.2/socket.io.min.js';
            cdnScript.onload = resolve;
            cdnScript.onerror = reject;
            document.head.appendChild(cdnScript);
        };
        document.head.appendChild(script);
    });
}

/**
 * 連接到協作伺服器
 */
export async function connect() {
    if (socket && isConnected) {
        return;
    }
    
    await loadSocketIO();
    
    socket = window.io('/whiteboard', {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
        reconnectionDelay: 1000,
    });
    
    // 連接事件
    socket.on('connect', () => {
        isConnected = true;
        reconnectAttempts = 0;
        console.log('[Collaboration] Connected to server');
        updateConnectionStatus(true);
    });
    
    socket.on('disconnect', () => {
        isConnected = false;
        console.log('[Collaboration] Disconnected from server');
        updateConnectionStatus(false);
    });
    
    socket.on('connect_error', (error) => {
        console.error('[Collaboration] Connection error:', error);
        reconnectAttempts++;
        if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            ui.showToast('無法連接到協作伺服器', 'error');
        }
    });
    
    // 協作事件
    socket.on('connected', (data) => {
        console.log('[Collaboration]', data.message, 'isGuest:', data.is_guest);
    });
    
    socket.on('room_created', (data) => {
        // 暫停廣播，防止狀態更新時觸發的 onChange 覆蓋場景
        broadcastPaused = true;
        
        currentRoomId = data.room_id;
        isOwner = true;
        updateCollaboratorsList(data.collaborators);
        showShareDialog(data.room_id, data.share_url);
        updateSharingStatus(true);
        console.log('[Collaboration] Room created:', data.room_id);
        
        // 延遲恢復廣播，確保 UI 更新完成
        setTimeout(() => {
            broadcastPaused = false;
            console.log('[Collaboration] Broadcasting resumed');
        }, 500);
    });
    
    socket.on('room_joined', (data) => {
        // 訪客加入時也暫停廣播
        broadcastPaused = true;
        
        currentRoomId = data.room_id;
        isOwner = data.is_owner;
        ownerId = data.owner_id;
        updateCollaboratorsList(data.collaborators);
        updateSharingStatus(true);
        ui.showToast('已加入協作房間', 'success');
        console.log('[Collaboration] Joined room:', data.room_id, 'isOwner:', isOwner);
        
        // 延遲恢復廣播
        setTimeout(() => {
            broadcastPaused = false;
            console.log('[Collaboration] Broadcasting resumed after join');
        }, 500);
    });
    
    socket.on('room_expired', (data) => {
        // 房間已過期（非當天）
        showExpiredDialog(data.message);
    });
    
    socket.on('collaborator_joined', (collaborator) => {
        collaborators.set(collaborator.socket_id, collaborator);
        updateCollaboratorsUI();
        const guestTag = collaborator.is_guest ? ' (訪客)' : '';
        ui.showToast(`${collaborator.username}${guestTag} 加入了協作`, 'info');
    });
    
    socket.on('collaborator_left', (data) => {
        // 找到並移除該用戶
        for (const [socketId, c] of collaborators) {
            if (c.user_id === data.user_id) {
                collaborators.delete(socketId);
                removeCursor(socketId);
                break;
            }
        }
        updateCollaboratorsUI();
        ui.showToast(`${data.username} 離開了協作`, 'info');
    });
    
    socket.on('collaborator_kicked', (data) => {
        collaborators.delete(data.socket_id);
        removeCursor(data.socket_id);
        updateCollaboratorsUI();
        ui.showToast(`${data.username} 已被移出`, 'info');
    });
    
    socket.on('kicked', (data) => {
        // 自己被踢出
        showKickedDialog(data.message, data.reason);
        cleanupCollaboration();
    });
    
    socket.on('sharing_ended', (data) => {
        // 分享已結束
        showSharingEndedDialog(data.message);
        cleanupCollaboration();
    });
    
    socket.on('sharing_status_changed', (data) => {
        updateSharingStatus(data.is_sharing);
        if (!data.is_sharing && data.guests_kicked > 0) {
            ui.showToast(`分享已結束，${data.guests_kicked} 位訪客已離開`, 'info');
        }
    });
    
    socket.on('request_scene', (data) => {
        // 有人請求場景同步
        console.log('[Collaboration] Received request_scene from:', data.requester?.username, 'socket:', data.requester?.socket_id);
        syncSceneToRequester(data.requester.socket_id);
    });
    
    socket.on('scene_synced', (data) => {
        // 收到場景同步資料
        console.log('[Collaboration] Received scene_synced, elements:', data.elements?.length);
        loadSyncedScene(data.elements, data.appState);
    });
    
    socket.on('scene_updated', (data) => {
        // 場景更新
        applySyncedElements(data.elements);
    });
    
    socket.on('element_updated', (data) => {
        // 單個元素更新
        applyElementUpdate(data.element);
    });
    
    socket.on('elements_removed', (data) => {
        // 元素刪除
        removeElements(data.element_ids);
    });
    
    socket.on('cursor_moved', (data) => {
        // 其他人的游標移動
        updateRemoteCursor(data);
    });
    
    socket.on('chat_received', (data) => {
        // 收到聊天訊息
        addChatMessage(data);
    });
    
    socket.on('error', (data) => {
        ui.showToast(data.message, 'error');
    });
    
    return new Promise((resolve) => {
        socket.once('connect', resolve);
        setTimeout(resolve, 3000); // 超時
    });
}

/**
 * 建立協作房間
 */
export async function createRoom(whiteboardId = null, isPublic = true) {
    if (!socket || !isConnected) {
        await connect();
    }
    
    socket.emit('create_room', {
        whiteboard_id: whiteboardId,
        is_public: isPublic
    });
}

/**
 * 加入協作房間
 */
export async function joinRoom(roomId, guestNameInput = null) {
    if (!socket || !isConnected) {
        await connect();
    }
    
    const data = { room_id: roomId };
    
    // 訪客模式需要帶上名稱
    if (guestNameInput || guestName) {
        data.guest_name = guestNameInput || guestName;
    }
    
    socket.emit('join_room', data);
}

/**
 * 離開協作房間
 */
export function leaveRoom() {
    if (!socket || !currentRoomId) {
        return;
    }
    
    socket.emit('leave_room', {
        room_id: currentRoomId
    });
    
    cleanupCollaboration();
    ui.showToast('已離開協作房間', 'info');
}

/**
 * 清理協作狀態
 */
function cleanupCollaboration() {
    currentRoomId = null;
    isOwner = false;
    ownerId = null;
    collaborators.clear();
    clearAllCursors();
    
    // 關閉雷射筆模式
    if (laserPointerActive) {
        toggleLaserPointer();
    }
    
    updateCollaboratorsUI();
    updateConnectionStatus(false);
    updateSharingStatus(false);
    
    // 移除 URL 參數
    const url = new URL(window.location);
    url.searchParams.delete('room');
    window.history.replaceState({}, '', url);
}

/**
 * 結束分享（主持人專用）
 */
export function endSharing() {
    if (!socket || !currentRoomId || !isOwner) {
        ui.showToast('只有主持人可以結束分享', 'warning');
        return;
    }
    
    // 確認對話框
    if (!confirm('確定要結束分享嗎？所有訪客將被移出協作。')) {
        return;
    }
    
    socket.emit('end_sharing', {
        room_id: currentRoomId
    });
}

/**
 * 踢出訪客（主持人專用）
 */
export function kickGuest(targetSocketId) {
    if (!socket || !currentRoomId || !isOwner) {
        ui.showToast('只有主持人可以踢出訪客', 'warning');
        return;
    }
    
    socket.emit('kick_guest', {
        room_id: currentRoomId,
        target_socket_id: targetSocketId
    });
}

/**
 * 廣播場景變更
 */
export function broadcastSceneUpdate(elements) {
    if (!socket || !currentRoomId) {
        return;
    }
    
    // 如果廣播暫停中，跳過
    if (broadcastPaused) {
        console.log('[Collaboration] Broadcasting paused, skipping');
        return;
    }
    
    // 深複製元素以避免序列化問題
    const elementsCopy = JSON.parse(JSON.stringify(elements));
    
    // 調試：檢查元素是否正確
    if (elementsCopy.length > 0) {
        const sample = elementsCopy[0];
        console.log('[Collaboration] Broadcasting scene update, elements:', elementsCopy.length,
            'First element type:', sample.type,
            'width:', sample.width, 'height:', sample.height);
    } else {
        console.log('[Collaboration] Broadcasting empty scene');
    }
    
    socket.emit('scene_update', {
        room_id: currentRoomId,
        elements: elementsCopy
    });
}

/**
 * 廣播單個元素更新（增量同步）
 */
export function broadcastElementUpdate(element) {
    if (!socket || !currentRoomId) {
        return;
    }
    
    socket.emit('element_update', {
        room_id: currentRoomId,
        element: element
    });
}

/**
 * 廣播元素刪除
 */
export function broadcastElementsDeleted(elementIds) {
    if (!socket || !currentRoomId) {
        return;
    }
    
    socket.emit('elements_deleted', {
        room_id: currentRoomId,
        element_ids: elementIds
    });
}

/**
 * 廣播游標位置
 */
export function broadcastCursorMove(x, y) {
    if (!socket || !currentRoomId) {
        return;
    }
    
    socket.emit('cursor_move', {
        room_id: currentRoomId,
        cursor: { x, y }
    });
}

/**
 * 切換雷射筆模式 - 使用 Excalidraw 內建的雷射筆工具
 */
export function toggleLaserPointer() {
    const api = state.getExcalidrawAPI();
    if (!api) return false;
    
    laserPointerActive = !laserPointerActive;
    
    if (laserPointerActive) {
        // 記住當前工具
        const appState = api.getAppState();
        previousTool = appState.activeTool?.type || 'selection';
        
        // 切換到雷射筆工具
        api.setActiveTool({ type: 'laser' });
    } else {
        // 恢復之前的工具
        api.setActiveTool({ type: previousTool || 'selection' });
        previousTool = null;
    }
    
    return laserPointerActive;
}

/**
 * 取得雷射筆狀態
 */
export function isLaserPointerActive() {
    return laserPointerActive;
}

/**
 * 發送聊天訊息
 */
export function sendChatMessage(message) {
    if (!socket || !currentRoomId) {
        return;
    }
    
    socket.emit('chat_message', {
        room_id: currentRoomId,
        message: message
    });
}

/**
 * 同步場景給請求者
 */
function syncSceneToRequester(targetSocketId) {
    const api = state.getExcalidrawAPI();
    if (!api) return;
    
    const elements = api.getSceneElements();
    const appState = api.getAppState();
    
    // 深複製元素以確保正確序列化
    const elementsCopy = JSON.parse(JSON.stringify(elements));
    
    // 調試：檢查元素是否正確
    if (elementsCopy.length > 0) {
        const sample = elementsCopy[0];
        console.log('[Collaboration] Syncing scene to requester, elements:', elementsCopy.length,
            'First element type:', sample.type,
            'width:', sample.width, 'height:', sample.height);
    } else {
        console.log('[Collaboration] Syncing empty scene to requester');
    }
    
    socket.emit('sync_scene', {
        room_id: currentRoomId,
        target: targetSocketId,
        elements: elementsCopy,
        appState: {
            viewBackgroundColor: appState.viewBackgroundColor
        }
    });
}

/**
 * 載入同步的場景
 */
function loadSyncedScene(elements, appState) {
    const api = state.getExcalidrawAPI();
    if (!api) return;
    
    console.log('[Collaboration] Loading synced scene, elements count:', elements?.length);
    
    // 確保 elements 是有效的陣列
    if (!Array.isArray(elements) || elements.length === 0) {
        console.log('[Collaboration] No elements to sync');
        return;
    }
    
    api.updateScene({
        elements: elements,
        appState: appState
    });
    
    console.log('[Collaboration] Scene synced from host');
}

/**
 * 套用同步的元素（智能合併，避免覆蓋正在繪製的元素）
 */
function applySyncedElements(newElements) {
    const api = state.getExcalidrawAPI();
    if (!api) return;
    
    // 調試：檢查收到的元素
    if (newElements && newElements.length > 0) {
        const sample = newElements[0];
        console.log('[Collaboration] Applying synced elements, count:', newElements.length,
            'First element type:', sample.type,
            'width:', sample.width, 'height:', sample.height);
    } else {
        console.log('[Collaboration] Received empty or invalid elements');
    }
    
    // 確保 newElements 是有效的陣列
    if (!Array.isArray(newElements)) {
        console.warn('[Collaboration] Invalid elements received');
        return;
    }
    
    const appState = api.getAppState();
    
    // 如果用戶正在繪製（有選中工具且正在拖拽），跳過此次更新
    if (appState.draggingElement || appState.resizingElement || appState.editingElement) {
        console.log('[Collaboration] Skipping update - user is actively drawing');
        return;
    }
    
    // 取得當前元素
    const currentElements = api.getSceneElements();
    
    // 建立 ID 到元素的映射
    const currentMap = new Map(currentElements.map(el => [el.id, el]));
    const newMap = new Map(newElements.map(el => [el.id, el]));
    
    // 合併元素：保留本地新元素，更新遠端元素
    const mergedElements = [];
    
    // 添加或更新來自遠端的元素
    for (const [id, el] of newMap) {
        const local = currentMap.get(id);
        if (local) {
            // 如果本地版本較新，保留本地版本
            if (local.version > el.version) {
                mergedElements.push(local);
            } else {
                mergedElements.push(el);
            }
        } else {
            mergedElements.push(el);
        }
    }
    
    // 添加只存在於本地的新元素（可能是正在繪製的）
    for (const [id, el] of currentMap) {
        if (!newMap.has(id)) {
            mergedElements.push(el);
        }
    }
    
    api.updateScene({
        elements: mergedElements
    });
}

/**
 * 套用單個元素更新
 */
function applyElementUpdate(updatedElement) {
    const api = state.getExcalidrawAPI();
    if (!api) return;
    
    const appState = api.getAppState();
    
    // 如果用戶正在繪製，跳過
    if (appState.draggingElement || appState.resizingElement || appState.editingElement) {
        return;
    }
    
    const elements = [...api.getSceneElements()];
    const index = elements.findIndex(el => el.id === updatedElement.id);
    
    if (index >= 0) {
        // 只有當遠端版本較新時才更新
        if (!elements[index].version || updatedElement.version >= elements[index].version) {
            elements[index] = updatedElement;
        }
    } else {
        elements.push(updatedElement);
    }
    
    api.updateScene({ elements });
}

/**
 * 移除元素
 */
function removeElements(elementIds) {
    const api = state.getExcalidrawAPI();
    if (!api) return;
    
    const elements = api.getSceneElements().filter(
        el => !elementIds.includes(el.id)
    );
    
    api.updateScene({ elements });
}

/**
 * 更新遠端游標
 */
function updateRemoteCursor(data) {
    const { user_id, username, color, cursor } = data;
    
    let cursorEl = cursorElements.get(user_id);
    
    if (!cursorEl) {
        cursorEl = createCursorElement(username, color);
        cursorElements.set(user_id, cursorEl);
        document.body.appendChild(cursorEl);
    }
    
    // 轉換座標到螢幕座標
    const api = state.getExcalidrawAPI();
    if (api) {
        const appState = api.getAppState();
        const screenX = (cursor.x - appState.scrollX) * appState.zoom.value;
        const screenY = (cursor.y - appState.scrollY) * appState.zoom.value;
        
        cursorEl.style.left = `${screenX}px`;
        cursorEl.style.top = `${screenY}px`;
        cursorEl.style.display = 'block';
    }
}

/**
 * 建立游標元素
 */
function createCursorElement(username, color) {
    const el = document.createElement('div');
    el.className = 'remote-cursor';
    el.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="${color}">
            <path d="M5.5 3.5L19 12L12 13.5L8 21L5.5 3.5Z" stroke="white" stroke-width="1"/>
        </svg>
        <span class="cursor-label" style="background:${color}">${username}</span>
    `;
    el.style.cssText = `
        position: fixed;
        pointer-events: none;
        z-index: 9999;
        transition: left 0.1s, top 0.1s;
        display: none;
    `;
    return el;
}

/**
 * 移除游標
 */
function removeCursor(userId) {
    const cursorEl = cursorElements.get(userId);
    if (cursorEl) {
        cursorEl.remove();
        cursorElements.delete(userId);
    }
}

/**
 * 清除所有游標
 */
function clearAllCursors() {
    cursorElements.forEach(el => el.remove());
    cursorElements.clear();
}

/**
 * 更新協作者列表
 */
function updateCollaboratorsList(collabList) {
    collaborators.clear();
    collabList.forEach(c => {
        collaborators.set(c.socket_id, c);
    });
    updateCollaboratorsUI();
}

/**
 * 更新協作者 UI
 */
function updateCollaboratorsUI() {
    const container = document.getElementById('collaborators-list');
    if (!container) return;
    
    if (collaborators.size === 0) {
        container.innerHTML = '<span class="text-muted">無協作者</span>';
        return;
    }
    
    // 分離主持人和訪客
    const collabArray = Array.from(collaborators.values());
    
    container.innerHTML = collabArray.map(c => {
        const isMe = c.user_id === ownerId && isOwner || 
                     (isGuestMode && c.username === guestName);
        const guestTag = c.is_guest ? '<small class="text-muted">(訪客)</small>' : '';
        const ownerTag = c.user_id === ownerId ? '<i class="fas fa-crown text-warning ms-1" title="主持人"></i>' : '';
        const meTag = isMe ? '<small class="text-success">(你)</small>' : '';
        
        // 主持人可以踢出訪客
        const kickBtn = isOwner && c.is_guest ? 
            `<button class="btn btn-sm btn-outline-danger ms-auto py-0 px-1 kick-guest-btn" 
                     data-socket-id="${c.socket_id}" title="移出">
                <i class="fas fa-times"></i>
            </button>` : '';
        
        return `
            <div class="collaborator-item d-flex align-items-center py-1">
                <span class="collaborator-badge me-2" style="background:${c.avatar_color}; width:24px; height:24px; display:inline-flex; align-items:center; justify-content:center; border-radius:50%; color:white; font-size:12px;">
                    ${c.username.charAt(0).toUpperCase()}
                </span>
                <span class="collaborator-name" style="font-size:13px;">
                    ${escapeHtml(c.username)}${ownerTag} ${guestTag} ${meTag}
                </span>
                ${kickBtn}
            </div>
        `;
    }).join('');
    
    // 綁定踢出按鈕事件
    container.querySelectorAll('.kick-guest-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const socketId = btn.dataset.socketId;
            if (confirm('確定要移出此訪客嗎？')) {
                kickGuest(socketId);
            }
        });
    });
    
    // 更新人數顯示
    const countEl = document.getElementById('collab-count');
    if (countEl) {
        countEl.textContent = collaborators.size;
    }
}

/**
 * 更新連接狀態
 */
function updateConnectionStatus(connected) {
    const indicator = document.getElementById('collab-status');
    if (!indicator) return;
    
    if (connected && currentRoomId) {
        indicator.innerHTML = `<i class="fas fa-circle text-success"></i> 協作中`;
        indicator.title = `房間: ${currentRoomId}`;
    } else if (connected) {
        indicator.innerHTML = `<i class="fas fa-circle text-warning"></i> 已連接`;
    } else {
        indicator.innerHTML = `<i class="fas fa-circle text-secondary"></i> 離線`;
    }
}

/**
 * 顯示分享對話框
 */
function showShareDialog(roomId, shareUrl) {
    const fullUrl = window.location.origin + shareUrl;
    
    // 建立對話框
    const dialog = document.createElement('div');
    dialog.className = 'modal fade';
    dialog.id = 'share-collab-modal';
    dialog.innerHTML = `
        <div class="modal-dialog">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title"><i class="fas fa-share-alt me-2"></i>分享協作連結</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                </div>
                <div class="modal-body">
                    <p class="mb-2">分享此連結給其他人加入協作：</p>
                    <div class="input-group mb-2">
                        <input type="text" class="form-control" value="${fullUrl}" readonly id="share-url-input" style="font-size: 0.85rem;">
                        <button class="btn btn-primary" type="button" id="copy-share-url" title="複製連結">
                            <i class="fas fa-copy me-1"></i>複製
                        </button>
                    </div>
                    <div class="alert alert-light border p-2 mb-3" style="word-break: break-all; font-family: monospace; font-size: 0.8rem;">
                        ${fullUrl}
                    </div>
                    <hr>
                    <p class="mb-2 text-center"><strong>或掃描 QR 碼加入：</strong></p>
                    <div class="text-center">
                        <div id="share-qr-code" style="display: inline-block; padding: 10px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);"></div>
                    </div>
                    <p class="text-muted text-center mt-2 mb-0" style="font-size: 0.75rem;">
                        <i class="fas fa-info-circle me-1"></i>房間 ID: ${roomId}
                    </p>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">關閉</button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(dialog);
    
    const modal = new bootstrap.Modal(dialog);
    modal.show();
    
    // 複製功能
    document.getElementById('copy-share-url').addEventListener('click', () => {
        navigator.clipboard.writeText(fullUrl).then(() => {
            ui.showToast('連結已複製到剪貼簿', 'success');
            const btn = document.getElementById('copy-share-url');
            btn.innerHTML = '<i class="fas fa-check me-1"></i>已複製';
            btn.classList.remove('btn-primary');
            btn.classList.add('btn-success');
            setTimeout(() => {
                btn.innerHTML = '<i class="fas fa-copy me-1"></i>複製';
                btn.classList.remove('btn-success');
                btn.classList.add('btn-primary');
            }, 2000);
        });
    });
    
    // 生成 QR Code
    generateQRCode(fullUrl, 'share-qr-code');
    
    // 清理
    dialog.addEventListener('hidden.bs.modal', () => {
        dialog.remove();
    });
    
    // 更新 URL
    const url = new URL(window.location);
    url.searchParams.set('room', roomId);
    window.history.replaceState({}, '', url);
}

/**
 * 生成 QR 碼（使用 Canvas 純 JavaScript 實現）
 */
function generateQRCode(text, containerId) {
    const container = document.getElementById(containerId);
    if (!container) return;
    
    // 檢查 QRCode 庫是否可用
    if (typeof QRCode !== 'undefined') {
        try {
            new QRCode(container, {
                text: text,
                width: 180,
                height: 180,
                colorDark: '#000000',
                colorLight: '#ffffff',
                correctLevel: QRCode.CorrectLevel.M
            });
            return;
        } catch (e) {
            console.warn('[Collaboration] QRCode library error:', e);
        }
    }
    
    // 備用方案：使用 Canvas 繪製簡易 QR 提示
    console.log('[Collaboration] QRCode library not available, using fallback');
    
    // 建立一個提示框，提供複製連結的方式
    container.innerHTML = `
        <div style="width: 180px; height: 180px; border: 2px dashed #ccc; border-radius: 8px; 
                    display: flex; flex-direction: column; align-items: center; justify-content: center;
                    background: #f9f9f9; padding: 10px; box-sizing: border-box;">
            <i class="fas fa-qrcode" style="font-size: 48px; color: #999; margin-bottom: 8px;"></i>
            <p style="margin: 0; font-size: 12px; color: #666; text-align: center;">
                請複製上方連結<br>分享給其他人
            </p>
        </div>
    `;
    
    // 嘗試延遲載入 QRCode
    setTimeout(() => {
        if (typeof QRCode !== 'undefined' && container.querySelector('.fa-qrcode')) {
            container.innerHTML = '';
            try {
                new QRCode(container, {
                    text: text,
                    width: 180,
                    height: 180,
                    colorDark: '#000000',
                    colorLight: '#ffffff',
                    correctLevel: QRCode.CorrectLevel.M
                });
            } catch (e) {
                console.warn('[Collaboration] Delayed QRCode generation failed:', e);
            }
        }
    }, 500);
}

/**
 * 新增聊天訊息
 */
function addChatMessage(data) {
    const chatContainer = document.getElementById('chat-messages');
    if (!chatContainer) return;
    
    const msgEl = document.createElement('div');
    msgEl.className = 'chat-message';
    msgEl.innerHTML = `
        <span class="chat-user" style="color:${data.color}">${data.username}:</span>
        <span class="chat-text">${escapeHtml(data.message)}</span>
    `;
    chatContainer.appendChild(msgEl);
    chatContainer.scrollTop = chatContainer.scrollHeight;
}

/**
 * HTML 轉義
 */
function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * 是否正在協作中
 */
export function isCollaborating() {
    return isConnected && currentRoomId !== null;
}

/**
 * 取得當前房間 ID
 */
export function getCurrentRoomId() {
    return currentRoomId;
}

/**
 * 取得協作者數量
 */
export function getCollaboratorsCount() {
    return collaborators.size;
}

/**
 * 是否為房間主持人
 */
export function isRoomOwner() {
    return isOwner;
}

/**
 * 設定主持人專屬控制
 */
function setupOwnerControls() {
    // 結束分享按鈕
    const endSharingBtn = document.getElementById('btn-end-sharing');
    if (endSharingBtn) {
        endSharingBtn.addEventListener('click', endSharing);
    }
}

/**
 * 更新分享狀態 UI
 */
function updateSharingStatus(sharing) {
    const startBtn = document.getElementById('btn-start-collab');
    const endBtn = document.getElementById('btn-end-sharing');
    const leaveBtn = document.getElementById('btn-leave-collab');
    const statusIndicator = document.getElementById('sharing-status');
    
    if (sharing && currentRoomId) {
        // 正在分享中
        if (statusIndicator) {
            statusIndicator.innerHTML = `
                <span class="badge bg-success me-2">
                    <i class="fas fa-broadcast-tower me-1"></i>分享中
                </span>
                <small class="text-muted" id="collab-count">${collaborators.size}</small>
                <small class="text-muted">人</small>
            `;
            statusIndicator.style.display = 'flex';
        }
        
        if (isOwner) {
            // 主持人：顯示結束分享按鈕
            if (startBtn) startBtn.style.display = 'none';
            if (endBtn) endBtn.style.display = 'inline-block';
            if (leaveBtn) leaveBtn.style.display = 'none';
        } else {
            // 參與者：顯示離開按鈕
            if (startBtn) startBtn.style.display = 'none';
            if (endBtn) endBtn.style.display = 'none';
            if (leaveBtn) leaveBtn.style.display = 'inline-block';
        }
    } else {
        // 未分享
        if (statusIndicator) {
            statusIndicator.innerHTML = '';
            statusIndicator.style.display = 'none';
        }
        
        if (!isGuestMode) {
            // 只有登入用戶可以開始協作
            if (startBtn) startBtn.style.display = 'inline-block';
        }
        if (endBtn) endBtn.style.display = 'none';
        if (leaveBtn) leaveBtn.style.display = 'none';
    }
}

/**
 * 顯示訪客名稱輸入對話框
 */
function showGuestNameDialog(roomId) {
    console.log('[Collaboration] Showing guest name dialog for room:', roomId);
    
    // 確保 Bootstrap 已載入
    if (typeof bootstrap === 'undefined' || !bootstrap.Modal) {
        console.error('[Collaboration] Bootstrap Modal not available, waiting...');
        // 延遲重試
        setTimeout(() => showGuestNameDialog(roomId), 500);
        return;
    }
    
    const dialog = document.createElement('div');
    dialog.className = 'modal fade';
    dialog.id = 'guest-name-modal';
    dialog.setAttribute('data-bs-backdrop', 'static');
    dialog.setAttribute('data-bs-keyboard', 'false');
    dialog.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header bg-primary text-white">
                    <h5 class="modal-title">
                        <i class="fas fa-user-plus me-2"></i>加入協作白板
                    </h5>
                </div>
                <div class="modal-body">
                    <p class="mb-3">請輸入你的名稱，讓其他人知道你是誰：</p>
                    <div class="mb-3">
                        <input type="text" class="form-control form-control-lg" 
                               id="guest-name-input" placeholder="你的名字" 
                               maxlength="20" autofocus>
                        <div class="form-text">最多 20 個字元</div>
                    </div>
                    <div class="alert alert-info mb-0">
                        <i class="fas fa-info-circle me-2"></i>
                        你將以訪客身份加入協作。此連結僅限今天使用。
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" id="guest-cancel-btn">
                        取消
                    </button>
                    <button type="button" class="btn btn-primary" id="guest-join-btn">
                        <i class="fas fa-sign-in-alt me-1"></i>加入協作
                    </button>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(dialog);
    console.log('[Collaboration] Dialog appended to body');
    
    try {
        const modal = new bootstrap.Modal(dialog);
        modal.show();
        console.log('[Collaboration] Modal shown');
        
        const nameInput = document.getElementById('guest-name-input');
        const joinBtn = document.getElementById('guest-join-btn');
        const cancelBtn = document.getElementById('guest-cancel-btn');
    
        // 自動聚焦
        dialog.addEventListener('shown.bs.modal', () => {
            nameInput.focus();
        });
        
        // Enter 鍵加入
        nameInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                joinBtn.click();
            }
        });
        
        // 加入按鈕
        joinBtn.addEventListener('click', async () => {
            const name = nameInput.value.trim();
            if (!name) {
                nameInput.classList.add('is-invalid');
                return;
            }
            
            guestName = name;
            modal.hide();
            
            // 加入房間
            await joinRoom(roomId, name);
        });
        
        // 取消按鈕
        cancelBtn.addEventListener('click', () => {
            modal.hide();
            // 導向首頁或登入頁
            window.location.href = '/';
        });
        
        // 清理
        dialog.addEventListener('hidden.bs.modal', () => {
            dialog.remove();
        });
    } catch (error) {
        console.error('[Collaboration] Error showing guest name dialog:', error);
        ui.showToast('無法顯示輸入對話框，請重新整理頁面', 'error');
    }
}

/**
 * 顯示房間過期對話框
 */
function showExpiredDialog(message) {
    const dialog = document.createElement('div');
    dialog.className = 'modal fade';
    dialog.id = 'expired-modal';
    dialog.setAttribute('data-bs-backdrop', 'static');
    dialog.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header bg-warning">
                    <h5 class="modal-title">
                        <i class="fas fa-clock me-2"></i>連結已過期
                    </h5>
                </div>
                <div class="modal-body text-center py-4">
                    <i class="fas fa-hourglass-end text-warning" style="font-size: 48px;"></i>
                    <p class="mt-3 mb-0">${escapeHtml(message)}</p>
                </div>
                <div class="modal-footer justify-content-center">
                    <a href="/" class="btn btn-primary">
                        <i class="fas fa-home me-1"></i>返回首頁
                    </a>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(dialog);
    const modal = new bootstrap.Modal(dialog);
    modal.show();
}

/**
 * 顯示被踢出對話框
 */
function showKickedDialog(message, reason) {
    const dialog = document.createElement('div');
    dialog.className = 'modal fade';
    dialog.id = 'kicked-modal';
    dialog.setAttribute('data-bs-backdrop', 'static');
    dialog.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header bg-danger text-white">
                    <h5 class="modal-title">
                        <i class="fas fa-user-times me-2"></i>已離開協作
                    </h5>
                </div>
                <div class="modal-body text-center py-4">
                    <i class="fas fa-door-open text-danger" style="font-size: 48px;"></i>
                    <p class="mt-3 mb-0">${escapeHtml(message)}</p>
                </div>
                <div class="modal-footer justify-content-center">
                    <a href="/" class="btn btn-primary">
                        <i class="fas fa-home me-1"></i>返回首頁
                    </a>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(dialog);
    const modal = new bootstrap.Modal(dialog);
    modal.show();
}

/**
 * 顯示分享結束對話框
 */
function showSharingEndedDialog(message) {
    const dialog = document.createElement('div');
    dialog.className = 'modal fade';
    dialog.id = 'sharing-ended-modal';
    dialog.setAttribute('data-bs-backdrop', 'static');
    dialog.innerHTML = `
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header bg-info text-white">
                    <h5 class="modal-title">
                        <i class="fas fa-stop-circle me-2"></i>協作已結束
                    </h5>
                </div>
                <div class="modal-body text-center py-4">
                    <i class="fas fa-hand-paper text-info" style="font-size: 48px;"></i>
                    <p class="mt-3 mb-0">${escapeHtml(message)}</p>
                </div>
                <div class="modal-footer justify-content-center">
                    <a href="/" class="btn btn-primary">
                        <i class="fas fa-home me-1"></i>返回首頁
                    </a>
                </div>
            </div>
        </div>
    `;
    
    document.body.appendChild(dialog);
    const modal = new bootstrap.Modal(dialog);
    modal.show();
}
